import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { PollResponse, TabulationPermission, User, Voter } from '../../types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SearchParams, removeIndexesOf, searchIndex, splitKeywords } from '../../utils/indexer';
import { addDoc, collection, deleteDoc, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

import { GoogleGenerativeAI } from "@google/generative-ai";
import { db } from '../../firebase';
import { fetchMunicipalitiesByDistrict } from '../../utils/locationUtils';
import { useAuth } from '../../hooks/useAuth';

const PurgeVoters: React.FC = () => {
  const { user } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [selectedMunicipality, setSelectedMunicipality] = useState('');

  const [failedPurges, setFailedPurges] = useState<{name: string, errors: string}[]>([]);
  const [deletedVoters, setDeletedVoters] = useState<string[]>([]);
  const [isPurgeComplete, setIsPurgeComplete] = useState(false);
  
  // Camera and image processing states
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [openScanDialog, setOpenScanDialog] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraDeviceId, setCurrentCameraDeviceId] = useState<string | null>(null);
  const [processedNames, setProcessedNames] = useState<{total: number, processed: number}>({
    total: 0,
    processed: 0
  });

  // Google Gemini API setup
  const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY ?? 'AIzaSyBmmS-_40L0zrMqQe0jq5vaojr-VGUp6vU');

  useEffect(() => {
    const loadMunicipalities = async () => {
      if (!user) return;

      try {
        // Default to REGION XIII, SURIGAO DEL NORTE, DISTRICT 1 as in other components
        const districtMunicipalities = await fetchMunicipalitiesByDistrict(
          'REGION XIII', 
          'SURIGAO DEL NORTE', 
          'DISTRICT 1'
        );

        // Filter municipalities based on user's allowedTabulations if set
        const filteredMunicipalities = user.allowedTabulations 
          ? districtMunicipalities.filter(municipality => 
              user.allowedTabulations?.some(
                (permission: TabulationPermission) => 
                  permission.type === 'municipal' && permission.id === municipality
              )
            )
          : districtMunicipalities;

        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Error loading municipalities:', error);
        setSnackbarMessage('Failed to load municipalities');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    loadMunicipalities();
  }, [user]);

  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
      setIsVideoReady(false);
    }
  }, [cameraStream]);

  const startCamera = useCallback(async (cameraDeviceId: string | null) => {
    try {
      // Enumerate available cameras
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      setAvailableCameras(videoDevices);

      // If no cameras found, throw an error
      if (videoDevices.length === 0) {
        throw new Error('No video devices found');
      }

      // Prioritize previously selected camera, fallback to first device
      const deviceId = cameraDeviceId || videoDevices[0].deviceId;
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { deviceId: { exact: deviceId } } 
      });
      
      setCameraStream(stream);
      setCurrentCameraDeviceId(deviceId);
      setIsVideoReady(false);

      const setupVideoStream = () => {
        const videoElement = videoRef.current;
        
        if (videoElement) {
          videoElement.srcObject = stream;
          videoElement.onloadedmetadata = () => {
            videoElement.play();
            setIsVideoReady(true);
          };
        } else {
          // If ref is not available, retry
          requestAnimationFrame(setupVideoStream);
        }
      };

      setupVideoStream();
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
      setIsVideoReady(false);
    }
  }, []);

  const switchCamera = useCallback(async (deviceId: string) => {
    // Stop current camera stream
    stopCamera();

    // Set new device ID
    setCurrentCameraDeviceId(deviceId);

    // Restart camera with new device
    await startCamera(deviceId);
  }, [stopCamera, startCamera]);

  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current && isVideoReady) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL('image/jpeg');
        processImage(imageDataUrl);
      }
    }
  }, [isVideoReady]);

  const processImage = async (imageDataUrl: string) => {
    setIsProcessing(true);
    setProcessedNames({ total: 0, processed: 0 });
    try {
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash-preview-04-17" });
      const prompt = `Extract names from this image. Identify voters to be purged. 
      Provide the result in a strict JSON format: 
      {"voters": ["Voter1 Full Name", "Voter2 Full Name"]}
      
      Rules:
      - Only return full names
      - If no names are clear, return an empty array
      - Do not make up names if they are not visible`;

      const result = await model.generateContent({
        contents: [{ role: "user", parts: [
          { text: prompt },
          { inlineData: { mimeType: "image/jpeg", data: imageDataUrl.split(',')[1] } }
        ]}]
      });
      
      const extractedText = result.response.text();
      
      // Remove any text outside of JSON brackets
      const jsonMatch = extractedText.match(/\{[^}]+\}/);
      if (!jsonMatch) {
        throw new Error('Could not parse names from image');
      }
      
      const parsedNames = JSON.parse(jsonMatch[0]);
      
      // Set total names to process
      const totalNames = parsedNames.voters?.length || 0;
      setProcessedNames({ total: totalNames, processed: 0 });

      // Call purge voters with extracted names
      await purgeVoters(parsedNames.voters || []);
    } catch (error) {
      console.error('Error processing image:', error);
      alert('Could not extract names from the image. Please try again.');
    } finally {
      setOpenScanDialog(false);
      setIsProcessing(false);
      stopCamera();
    }
  };

  const searchVoter = async (keyword: string): Promise<Voter[]> => {
    const keywords = splitKeywords(keyword).sort((a, b) => b.length - a.length);
    const searchParams: SearchParams = {
      keywords,
      resultLimit: 10,
      document: "voter",
      field: "name",
      searchAfter: null
    };
    let result = await searchIndex(searchParams);
    while ((result.indexes.length <= 0) && (keywords.length > 2)) {
      keywords.pop();
      searchParams.keywords = keywords;
      result = await searchIndex(searchParams);
    }
    const matches: Voter[] = [];
    for (const index of result.indexes) {
      const voterDocRef = doc(db, index.documentPath);
      const voterDoc = await getDoc(voterDocRef);
      if (voterDoc.exists()) {
        const voter = voterDoc.data() as Voter;
        matches.push(voter);
      }
    }

    return matches;
  };

  const getLatestPollResponse = async (voter: Voter, user: User): Promise<PollResponse | null> => {
    const latestPollResponsePath =
      `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
      + `provinces/${voter.province}/districts/${voter.district}/`
      + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
      + `precincts/${voter.precinct}/voters/${voter.name}`;
    const latestPollResponseDocRef = doc(db, latestPollResponsePath);
    const latestPollResponseDoc = await getDoc(latestPollResponseDocRef);
    return latestPollResponseDoc.exists() ? latestPollResponseDoc.data() as PollResponse : null;
  };

  const deleteVoter = async (voter: Voter, user: User) => {
    try {
      const deletedVotersRef = collection(db, 'deletedVoters');
      const deletedVoterDocRef = doc(deletedVotersRef, voter.id);
      await setDoc(deletedVoterDocRef, voter);
      
      const docRef = doc(db, 'voters', voter.id);          
      const documentPath = docRef.path;

      await deleteDoc(docRef);
      await removeIndexesOf("voter", "name", documentPath);

      const latestPollResponsePath =
        `tabulationsV2/${user.dashboard}/regions/${voter.region}/`
        + `provinces/${voter.province}/districts/${voter.district}/`
        + `cities/${voter.city_municipality}/barangays/${voter.barangay}/`
        + `precincts/${voter.precinct}/voters/${voter.name}`;
      const latestPollResponseDocRef = doc(db, latestPollResponsePath);
      const latestPollResponseDoc = await getDoc(latestPollResponseDocRef);
      if (latestPollResponseDoc.exists()) {
        await deleteDoc(latestPollResponseDocRef);
      }
      console.log('Deleted voter:', voter.name);
      
      // Add the deleted voter's name to the list
      setDeletedVoters(prevDeletedVoters => [...prevDeletedVoters, voter.name]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
        name: voter.name,
        errors: errorMessage
      }]);
      console.error(`Error deleting voter ${voter.name}:`, error);
    }
  };

  const saveFailedPurge = async (name: string, errors: string) => {
    if (!user) return;

    try {
      const failedPurgesRef = collection(db, 'failedPurges');
      await addDoc(failedPurgesRef, {
        voterName: name,
        errors: errors,
        municipality: selectedMunicipality,
        userId: user.uid,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      console.error('Error saving failed purge:', error);
    }
  };

  const purgeVoters = async (names: string[]) => {
    // Reset previous state
    setFailedPurges([]);
    setDeletedVoters([]);
    setIsPurgeComplete(false);

    if (!user) {
      setSnackbarMessage('You must be logged in to perform this action');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!selectedMunicipality) {
      setSnackbarMessage('Please select a municipality');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    for (const name of names) {
      // Update processed names progress
      setProcessedNames(prev => ({
        ...prev,
        processed: prev.processed + 1
      }));

      const matches = await searchVoter(name);
      if (matches.length === 0) {
        const errorMessage = 'Voter not found';
        setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
          name: name,
          errors: errorMessage
        }]);
        await saveFailedPurge(name, errorMessage);
        continue;
      }
      if (matches.length > 2) {
        const errorMessage = 'More than 2 voters match.';
        setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
          name: name,
          errors: errorMessage
        }]);
        await saveFailedPurge(name, errorMessage);
        continue;
      }
      
      let voterForDelete: Voter | null = null;
      let voterToKeep: Voter | null = null;
      for (const match of matches) {
        if (match.city_municipality === selectedMunicipality) {
          voterForDelete = match;
        } else {
          voterToKeep = match;
        }
      }
      if (!voterForDelete) {
        const errorMessage = "Unable to find voter record for delete";
        setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
          name: name,
          errors: errorMessage
        }]);
        await saveFailedPurge(name, errorMessage);
        continue;
      }
      if (!voterToKeep) {
        const errorMessage = "Unable to find voter record to keep.";
        setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
          name: name,
          errors: errorMessage
        }]);
        await saveFailedPurge(name, errorMessage);
        continue;
      }

      let shouldDelete = false;
      let errors = "Unmarked for deletion.";
      const toKeepPollResponse = await getLatestPollResponse(voterToKeep, user);
      if (toKeepPollResponse) {
        if (toKeepPollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering') {
          shouldDelete = true;
        }
      }

      const forDeletePollResponse = await getLatestPollResponse(voterForDelete, user);
      if (!toKeepPollResponse && !forDeletePollResponse) {
        // delete voter that doent have any poll
        shouldDelete = true;
      }
      
      if (!shouldDelete) {
        if (forDeletePollResponse) {
          if (toKeepPollResponse) {
            if (forDeletePollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === toKeepPollResponse.responses['DISTRICT 1 REPRESENTATIVE']) {
              shouldDelete = true;
            }
            if ((forDeletePollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering') && 
              (forDeletePollResponse.responses['DISTRICT 1 REPRESENTATIVE'] !== toKeepPollResponse.responses['DISTRICT 1 REPRESENTATIVE'])
            ) {
              errors = 'Conflicting polls for previous and transferred municipality.';
            }
          } else {
            if (forDeletePollResponse.responses['DISTRICT 1 REPRESENTATIVE'] === 'Lucille Sering') {
              errors = 'No Abante poll entry to transferred municipality';
            } else {
              // Padajon voter with no poll to transferred municipality
              shouldDelete = true;
            }
          }
        }
      }

      if (!shouldDelete && !forDeletePollResponse && toKeepPollResponse) {
        if (toKeepPollResponse.responses['DISTRICT 1 REPRESENTATIVE'] !== 'Lucille Sering') {
          shouldDelete = true;
        }
      }

      if (shouldDelete) {
        await deleteVoter(voterForDelete, user);
      } else {
        setFailedPurges(prevFailedPurges => [...prevFailedPurges, {
          name: name,
          errors: errors
        }]);
        await saveFailedPurge(name, errors);
      }
    }

    // Mark purge as complete
    setIsPurgeComplete(true);
  };

  const handlePurgeVoters = async () => {
    // Open scan dialog to capture voter names
    setOpenScanDialog(true);
    setTimeout(() => {
      startCamera(currentCameraDeviceId);
    }, 100);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Purge Voters
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Voters' Past Municipality *</InputLabel>
            <Select
              value={selectedMunicipality}
              label="Municipality *"
              onChange={(e) => setSelectedMunicipality(e.target.value)}
              required
            >
              {municipalities.map(municipality => (
                <MenuItem key={municipality} value={municipality}>
                  {municipality}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handlePurgeVoters}
            disabled={!selectedMunicipality}
          >
            Purge Voters
          </Button>
        </Grid>
      </Grid>

      {isPurgeComplete && (
        <>
          {deletedVoters.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Deleted Voters
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Voter Name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deletedVoters.map((name, index) => (
                      <TableRow key={index}>
                        <TableCell>{name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {failedPurges.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Failed Purges
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Voter Name</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedPurges.map((purge, index) => (
                      <TableRow key={index}>
                        <TableCell>{purge.name}</TableCell>
                        <TableCell>{purge.errors}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Camera Scan Dialog */}
      <Dialog 
        open={openScanDialog} 
        onClose={() => {
          setOpenScanDialog(false);
          stopCamera();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Scan Voters to Purge</DialogTitle>
        <DialogContent>
          <Box>
            {isProcessing ? (
              <Box sx={{ width: '100%', textAlign: 'center', mt: 4, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Processing Voter Names
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={processedNames.total > 0 
                    ? (processedNames.processed / processedNames.total) * 100 
                    : 0} 
                  sx={{ width: '100%', mt: 2 }} 
                />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  {processedNames.total > 0 
                    ? `Processed ${processedNames.processed} of ${processedNames.total} names` 
                    : 'Extracting and matching voter names...'}
                </Typography>
              </Box>
            ) : (
              <>
                <video 
                  ref={videoRef} 
                  style={{ width: '100%', maxHeight: 480 }}
                  playsInline
                />
                <canvas 
                  ref={canvasRef} 
                  style={{ display: 'none' }} 
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Select Camera</InputLabel>
                  <Select
                    value={currentCameraDeviceId ?? availableCameras[0]?.deviceId}
                    label="Select Camera"
                    onChange={(e) => switchCamera(e.target.value)}
                    disabled={availableCameras.length <= 1}
                  >
                    {availableCameras.map((camera) => (
                      <MenuItem key={camera.deviceId} value={camera.deviceId}>
                        {camera.label || `Camera ${availableCameras.findIndex(c => c.deviceId === camera.deviceId) + 1}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={captureImage}
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Capture Image'}
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenScanDialog(false);
            stopCamera();
          }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurgeVoters;
